import React, { useState } from "react";
import {
  Box, Container, Typography, Grid, TextField, Button, Paper, Stack,
  Snackbar, Alert, Divider
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SendIcon from "@mui/icons-material/Send";

const accent = "#0d47a1";
const accentLight = "#e3f2fd";

const contactInfo = [
  { icon: <EmailIcon />, label: "Email", value: "support@riteautomation.com", href: "mailto:support@riteautomation.com" },
  { icon: <PhoneIcon />, label: "Phone", value: "+1 (555) 123-4567", href: "tel:+15551234567" },
  { icon: <LocationOnIcon />, label: "Address", value: "123 Innovation Drive, Tech Park, CA 94025" },
  { icon: <AccessTimeIcon />, label: "Business Hours", value: "Mon – Fri, 9 AM – 6 PM IST" },
];

const ContactPage = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [errors, setErrors] = useState({});
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Valid email required";
    if (!form.message.trim()) e.message = "Message is required";
    if (form.message.length > 1000) e.message = "Max 1000 characters";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSnack({ open: true, msg: "Message sent! We'll get back to you within 24 hours.", severity: "success" });
    setForm({ name: "", email: "", phone: "", subject: "", message: "" });
  };

  return (
    <>
      <Helmet>
        <title>Contact Us — Rite Marketing Automation</title>
        <meta name="description" content="Get in touch with the Rite Marketing Automation team. Request a demo, ask about pricing, or get technical support." />
      </Helmet>

      {/* Hero */}
      <Box sx={{ background: `linear-gradient(135deg, ${accent} 0%, #1565c0 100%)`, color: "#fff", py: { xs: 8, md: 12 }, textAlign: "center" }}>
        <Container maxWidth="md">
          <Typography variant="h3" fontWeight={800} gutterBottom>Get in Touch</Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, maxWidth: 600, mx: "auto" }}>
            Have questions about our platform? Want a personalised demo? We'd love to hear from you.
          </Typography>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Grid container spacing={6}>
          {/* Form */}
          <Grid item xs={12} md={7}>
            <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, border: "1px solid #e0e0e0", borderRadius: 3 }}>
              <Typography variant="h5" fontWeight={700} color={accent} gutterBottom>Send Us a Message</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Fill out the form below and our team will respond within 24 hours.
              </Typography>

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Full Name *" value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      error={!!errors.name} helperText={errors.name}
                      inputProps={{ maxLength: 100 }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Email Address *" type="email" value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      error={!!errors.email} helperText={errors.email}
                      inputProps={{ maxLength: 255 }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Phone Number" value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      inputProps={{ maxLength: 20 }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Subject" value={form.subject}
                      onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      inputProps={{ maxLength: 150 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Your Message *" multiline rows={5} value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      error={!!errors.message} helperText={errors.message || `${form.message.length}/1000`}
                      inputProps={{ maxLength: 1000 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Button type="submit" variant="contained" size="large" endIcon={<SendIcon />}
                      sx={{ background: accent, px: 5, py: 1.5, borderRadius: 2, fontWeight: 700, textTransform: "none",
                        "&:hover": { background: "#0a3a8a" } }}>
                      Send Message
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>

          {/* Info sidebar */}
          <Grid item xs={12} md={5}>
            <Stack spacing={3}>
              <Paper sx={{ p: 4, background: accentLight, borderRadius: 3, border: `1px solid ${accent}20` }}>
                <Typography variant="h6" fontWeight={700} color={accent} gutterBottom>Contact Information</Typography>
                <Stack spacing={2.5} sx={{ mt: 2 }}>
                  {contactInfo.map((c, i) => (
                    <Stack key={i} direction="row" spacing={2} alignItems="flex-start">
                      <Box sx={{ color: accent, mt: 0.3 }}>{c.icon}</Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">{c.label}</Typography>
                        {c.href ? (
                          <Typography variant="body2" component="a" href={c.href}
                            sx={{ display: "block", color: accent, textDecoration: "none", fontWeight: 600,
                              "&:hover": { textDecoration: "underline" } }}>
                            {c.value}
                          </Typography>
                        ) : (
                          <Typography variant="body2" fontWeight={600}>{c.value}</Typography>
                        )}
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              </Paper>

              <Paper sx={{ p: 4, borderRadius: 3, border: "1px solid #e0e0e0", textAlign: "center" }}>
                <Typography variant="h6" fontWeight={700} color={accent} gutterBottom>Request a Live Demo</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  See how Rite can transform your restaurant or healthcare business.
                </Typography>
                <Button variant="outlined" size="large"
                  sx={{ borderColor: accent, color: accent, borderRadius: 2, textTransform: "none", fontWeight: 700,
                    "&:hover": { background: accentLight, borderColor: accent } }}>
                  Schedule Demo
                </Button>
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      </Container>

      {/* Map placeholder */}
      <Box sx={{ background: "#f5f5f5", py: 1 }}>
        <iframe title="Rite Office Location" width="100%" height="350" style={{ border: 0 }}
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3168.639!2d-122.084!3d37.422!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzfCsDI1JzE5LjIiTiAxMjLCsDA1JzAyLjQiVw!5e0!3m2!1sen!2sus!4v1600000000000"
          loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
      </Box>

      <Snackbar open={snack.open} autoHideDuration={5000} onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })} sx={{ width: "100%" }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ContactPage;
